import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { supabase } from '../../../lib/supabase';
import { fetchAll } from '../../../lib/save';
import { siteZone } from '../../../lib/when';
import { decodeCatalogBytes } from '../../../lib/catalogParse';
import { unwrap } from './transferOrders';
import { newestLoad } from './lineFix';

/*
  THE MORNING REPORTS. Ordering stage 3, 11 October 2026.

  His words of 4 October: *"the stock status and excess calc only get paste at
  the begining of the day not for every run."* So they are uploaded once a day,
  on the Job Queue, and every run that day reads them.

    ClaimSoft Stock Status   par, on hand and on order for every warehouse.
                             The conversion audit adds these up over a family.
    Account Edge Excess Calc Austin's real stock. AUSTIN NORTH in ClaimSoft is a
                             dummy, his words; Austin lives in Account Edge.

  **The Excess Calc's arithmetic is his workbook's, copied rather than
  improved**: true on hand is on hand less what is committed, and Austin has
  spare only above the item's minimum level in Account Edge, read from the
  newest Account Edge item list.

  A row with nothing in it (no par, nothing on hand or on order) is counted and
  not stored: it adds nothing to any sum.

  **Pending, quantity needed and quantity to reorder are ignored**, his answer
  of 12 October 2026: *"ignore pending qty, quantity needed and quantity to
  reorder on the stock status report."* They are kept on the row as the report
  sent them, and nothing reads them. Only par, on hand and on order count. **Each upload is kept**, and
  only one that saved whole is ever the one a run reads.
*/

const KINDS = {
  stock_status: {
    label: 'ClaimSoft Stock Status',
    table: 'stock_status_rows',
    cols: {
      item:      ['ItemNumber', 'Item Number'],
      warehouse: ['WarehouseName', 'Warehouse Name', 'Warehouse'],
      par:       ['Par Level', 'ParLevel'],
      onHand:    ['QuantityOnHand', 'Quantity On Hand', 'QOH'],
      onOrder:   ['QuantityOnOrder', 'Quantity On Order', 'QOO'],
      pending:   ['PendingQuantity', 'Pending Quantity'],
      needed:    ['QuantityNeeded'],
      toReorder: ['QuantityToReorder'],
      desc:      ['ItemDescription', 'Description'],
      vendor:    ['VendorName', 'Vendor'],
      perUom:    ['ItemsPerUOM'],
      cost:      ['CostPerItem'],
    },
    needs: ['item', 'warehouse', 'par', 'onHand', 'onOrder'],
  },
  excess_calc: {
    label: 'Account Edge Excess Calc',
    table: 'excess_calc_rows',
    cols: {
      item:      ['Item #', 'Item Number', 'Item No.', 'ItemNumber'],
      name:      ['Item Name', 'Name'],
      onHand:    ['On Hand', 'Units On Hand'],
      committed: ['Committed'],
      onOrder:   ['On Order'],
      available: ['Available'],
    },
    needs: ['item', 'onHand', 'committed'],
  },
};
export const REPORT_LABEL = { stock_status: KINDS.stock_status.label, excess_calc: KINDS.excess_calc.label };

/* A number as a report writes it: commas, a dollar sign, brackets for a minus.
   Blank is nothing, which is 0. Anything else unreadable is null, and counted. */
export function num(v) {
  let s = String(v ?? '').trim().replace(/[$,\s]/g, '');
  if (s === '') return 0;
  let neg = false;
  if (/^\(.*\)$/.test(s)) { neg = true; s = s.slice(1, -1); }
  const n = Number(s);
  return Number.isFinite(n) ? (neg ? -n : n) : null;
}

/* The header row, wherever it is in the first forty, and which column is which. */
function findColumns(table, kind) {
  const spec = KINDS[kind];
  const norm = s => String(s ?? '').trim().toLowerCase();
  for (let at = 0; at < Math.min(table.length, 40); at++) {
    const head = table[at].map(norm);
    const idx = {};
    for (const [key, names] of Object.entries(spec.cols)) {
      const i = head.findIndex(h => names.some(n => norm(n) === h));
      if (i >= 0) idx[key] = i;
    }
    if (spec.needs.every(k => idx[k] != null)) return { at, idx };
  }
  return null;
}

/*
  **The file, read into rows.** Pure, so it can be tested. Refuses a file that
  is not the report it was given as, naming the columns it could not find, so
  the Excess Calc put in the Stock Status box says so instead of loading wrong.
*/
export function parseReport(table, kind) {
  const spec = KINDS[kind];
  const found = findColumns(table, kind);
  if (!found) {
    const want = spec.needs.map(k => spec.cols[k][0]).join(', ');
    return { rows: [], problem: 'This does not look like the ' + spec.label + '. It needs the columns ' + want + '.' };
  }
  const { at, idx } = found;
  const cell = (r, k) => (idx[k] == null ? '' : r[idx[k]]);
  const headerItem = String(table[at][idx.item]).trim().toUpperCase();
  const rows = [];
  let empty = 0, unreadable = 0, inFile = 0;
  table.slice(at + 1).forEach((r, i) => {
    const item = unwrap(cell(r, 'item'));
    if (!item || item.toUpperCase() === headerItem) return;   // blank, or the header said twice
    inFile++;
    const n = k => { const v = num(cell(r, k)); if (v === null) unreadable++; return v || 0; };
    if (kind === 'stock_status') {
      const row = {
        source_row: at + i + 2, item_number: item, description: String(cell(r, 'desc') || '').trim() || null,
        warehouse_name: String(cell(r, 'warehouse') || '').trim(), vendor_name: String(cell(r, 'vendor') || '').trim() || null,
        par_level: n('par'), on_hand: n('onHand'), on_order: n('onOrder'), pending: n('pending'),
        needed: idx.needed == null ? null : n('needed'), to_reorder: idx.toReorder == null ? null : n('toReorder'),
        items_per_uom: idx.perUom == null ? null : n('perUom'), cost_per_item: idx.cost == null ? null : n('cost'),
      };
      if (!row.warehouse_name) { unreadable++; return; }
      if (!row.par_level && !row.on_hand && !row.on_order) { empty++; return; }
      rows.push(row);
    } else {
      const row = {
        source_row: at + i + 2, item_number: item, item_name: String(cell(r, 'name') || '').trim() || null,
        on_hand: n('onHand'), committed: n('committed'), on_order: n('onOrder'), available: n('available'),
      };
      if (!row.on_hand && !row.committed && !row.on_order && !row.available) { empty++; return; }
      rows.push(row);
    }
  });
  return { rows, inFile, empty, unreadable, problem: inFile ? null : 'There are no item rows in this file.' };
}

/* A spreadsheet or a text export, into a table of cells. Text is read as text,
   so a ClaimSoft number like 00002 keeps its zeros. */
export async function readTable(file) {
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (/\.(xlsx|xlsm|xls)$/i.test(file.name)) {
    const wb = XLSX.read(bytes, { type: 'array' });
    return { bytes, table: XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, defval: '', raw: false }) };
  }
  const parsed = Papa.parse(decodeCatalogBytes(bytes), { skipEmptyLines: 'greedy' });
  return { bytes, table: parsed.data };
}

async function fingerprint(bytes) {
  try {
    const h = await crypto.subtle.digest('SHA-256', bytes);
    return [...new Uint8Array(h)].map(b => b.toString(16).padStart(2, '0')).join('');
  } catch { return 'size-' + bytes.length; }
}

/* Today, in the site's own time zone, as 2026-10-11. */
export function today(at = new Date()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: siteZone(), year: 'numeric', month: '2-digit', day: '2-digit' }).format(at);
}

/*
  **Upload one report.** The report row is written first and marked complete
  only once every row is in and counted back, so a half-loaded report is never
  the one a run reads. If anything fails, the report is removed again.
*/
export async function uploadReport(file, kind, userId, onProgress) {
  const { bytes, table } = await readTable(file);
  const parsed = parseReport(table, kind);
  if (parsed.problem) return { ok: false, problem: parsed.problem };
  const hash = await fingerprint(bytes);
  const day = today();

  const { data: same } = await supabase.from('morning_reports').select('id, loaded_at')
    .eq('kind', kind).eq('file_hash', hash).eq('report_day', day).eq('complete', true).limit(1);
  if (same && same.length) return { ok: false, problem: 'This exact file was already loaded today.' };

  const { data: report, error } = await supabase.from('morning_reports').insert({
    kind, report_day: day, file_name: file.name, file_hash: hash, rows_in_file: parsed.inFile, loaded_by: userId || null,
  }).select('id').maybeSingle();
  if (error || !report) return { ok: false, problem: 'The report did not save: ' + (error ? error.message : 'nothing came back') };

  const fail = async why => {
    await supabase.from('morning_reports').delete().eq('id', report.id);
    return { ok: false, problem: why };
  };
  const table_ = KINDS[kind].table;
  const B = 1000;
  for (let i = 0; i < parsed.rows.length; i += B) {
    const { error: e } = await supabase.from(table_).insert(parsed.rows.slice(i, i + B).map(r => ({ ...r, report_id: report.id })));
    if (e) return fail('Rows did not save: ' + e.message + '. Nothing from this file was kept.');
    if (onProgress) onProgress(Math.min(i + B, parsed.rows.length), parsed.rows.length);
  }
  const { count, error: cErr } = await supabase.from(table_).select('id', { count: 'exact', head: true }).eq('report_id', report.id);
  if (cErr || count !== parsed.rows.length) {
    return fail('Only ' + (count || 0) + ' of ' + parsed.rows.length + ' rows saved, so nothing from this file was kept.');
  }
  const { data: done } = await supabase.from('morning_reports').update({ complete: true, rows_kept: count })
    .eq('id', report.id).select('id').maybeSingle();
  if (!done) return fail('The report could not be marked complete, so nothing from this file was kept.');
  return { ok: true, kept: count, inFile: parsed.inFile, empty: parsed.empty, unreadable: parsed.unreadable };
}

/* The newest complete report of each kind. */
export async function newestReports() {
  const out = {};
  for (const kind of Object.keys(KINDS)) {
    const { data, error } = await supabase.from('morning_reports')
      .select('id, kind, report_day, file_name, rows_in_file, rows_kept, loaded_at, loaded_by')
      .eq('kind', kind).eq('complete', true).order('loaded_at', { ascending: false }).limit(1);
    if (error) return { reports: out, error };
    out[kind] = (data && data[0]) || null;
  }
  return { reports: out, error: null };
}

/*
  **Stock at one warehouse, for some ClaimSoft numbers**, off one Stock Status.
  An item can be on more than one row for a warehouse (a bin each), so the rows
  are added up.
*/
export async function stockAt(reportId, warehouseName, numbers) {
  const keys = [...new Set(numbers.map(n => String(n).trim().toUpperCase()).filter(Boolean))];
  const out = new Map();
  if (!reportId || !warehouseName || !keys.length) return { stock: out, error: null };
  for (let i = 0; i < keys.length; i += 150) {
    const { data, error } = await fetchAll(() => supabase.from('stock_status_rows')
      .select('id, item_key, par_level, on_hand, on_order')
      .eq('report_id', reportId).eq('warehouse_key', warehouseName.trim().toUpperCase())
      .in('item_key', keys.slice(i, i + 150)).order('id'));
    if (error) return { stock: out, error };
    for (const r of data || []) {
      const had = out.get(r.item_key) || { par: 0, onHand: 0, onOrder: 0 };
      had.par += Number(r.par_level); had.onHand += Number(r.on_hand);
      had.onOrder += Number(r.on_order);
      out.set(r.item_key, had);
    }
  }
  return { stock: out, error: null };
}

/* His Excess Calc columns G to I, as the workbook writes them. */
export function austinSpare({ onHand = 0, minLevel = 0 }) {
  /* **His rule of 12 October 2026**, replacing the workbook's arithmetic copied
     at stage 3 (which took committed off first): *"look at the Min Level for
     the Account Edge item and compare that to the on hand qty from the excess
     calc ... if the on hand is lower than the min level Austin does not have
     excess if the on hand is higher than the min level austin has excess."*
     So spare is on hand above the min level, and nothing at or below it.
     Committed is kept on the row as the report sent it, and not used. */
  return { spare: Math.max(0, onHand - (minLevel || 0)) };
}

/* Austin's stock for some Account Edge numbers, off one Excess Calc, with each
   item's minimum level from the newest Account Edge item list. */
export async function austinStock(reportId, aeNumbers) {
  const given = [...new Set(aeNumbers.map(n => String(n).trim()).filter(Boolean))];
  const keys = [...new Set(given.map(n => n.toUpperCase()))];
  const out = new Map();
  if (!reportId || !keys.length) return { austin: out, error: null };
  const mins = new Map();
  const load = await newestLoad('account_edge');
  for (let i = 0; i < keys.length; i += 150) {
    const part = keys.slice(i, i + 150);
    const { data, error } = await supabase.from('excess_calc_rows')
      .select('item_key, on_hand, committed, on_order, available').eq('report_id', reportId).in('item_key', part);
    if (error) return { austin: out, error };
    if (load && load.id) {
      const { data: ae } = await supabase.from('account_edge_items_raw')
        .select('"Item Number","Minimum Level"').eq('load_id', load.id)
        .in('"Item Number"', [...new Set([...part, ...given.filter(g => part.includes(g.toUpperCase()))])]);
      for (const a of ae || []) mins.set(String(a['Item Number']).trim().toUpperCase(), num(a['Minimum Level']) || 0);
    }
    for (const r of data || []) {
      const minLevel = mins.get(r.item_key) || 0;
      const onHand = Number(r.on_hand), committed = Number(r.committed);
      out.set(r.item_key, { onHand, committed, onOrder: Number(r.on_order), available: Number(r.available), minLevel, ...austinSpare({ onHand, minLevel }) });
    }
  }
  return { austin: out, error: null };
}
